import { useMemo, useState } from 'react';
import { Button, Dropdown, Header, Label, Modal, Segment } from 'semantic-ui-react';

import { coversDate } from './admin/adminHelpers';

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const fmtKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TODAY = fmtKey(new Date());

const fmtMonth = (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const fmtDateLabel = (key) =>
  new Date(key + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const buildDays = (monthDate) => {
  const year  = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start  = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { key: fmtKey(d), num: d.getDate(), inMonth: d.getMonth() === month };
  });
};

/* Reserved = every room booked for the day, whether or not the guest has
   arrived yet. Occupied is a subset of Reserved — the guest has checked in.
   Everything else (Pending, Rejected, Cancelled, Transferred, No Show, Checked Out)
   doesn't block the room. */
const RESERVED_STATUSES = new Set(['Approved', 'Partially Confirmed']);
const OCCUPIED_STATUSES = new Set(['Checked In']);

const getDayStats = (guestHouses, dateKey) => {
  let total = 0, reserved = 0, occupied = 0;
  guestHouses.forEach(gh => {
    total += gh.rooms.filter(r => r.status !== 'Maintenance').length;
    gh.requests.forEach(req => {
      if (!coversDate(req.checkIn, req.checkOut, dateKey)) return;

      const isOccupied = OCCUPIED_STATUSES.has(req.status);
      const isReserved = RESERVED_STATUSES.has(req.status);
      if (!isOccupied && !isReserved) return;

      const n = req.assignedRooms?.length
        ? req.assignedRooms.length
        : req.assignedRoom
          ? 1
          : req.roomsRequired || 1;

      reserved += n;
      if (isOccupied) occupied += n;
    });
  });
  const vacant = Math.max(0, total - reserved);
  return { total, vacant, reserved, occupied };
};

/* Per-room breakdown for the day-detail modal: a claimed room always lands in
   Reserved, and additionally in Occupied if the guest has checked in — the
   two lists overlap by design. Whatever's left over is vacant. */
const getRoomDetails = (guestHouses, dateKey) => {
  const vacant = [];
  const reserved = [];
  const occupied = [];

  guestHouses.forEach(gh => {
    const claimedRoomNumbers = new Set();

    gh.requests.forEach(req => {
      if (!coversDate(req.checkIn, req.checkOut, dateKey)) return;

      const isOccupied = OCCUPIED_STATUSES.has(req.status);
      const isReserved = RESERVED_STATUSES.has(req.status);
      if (!isOccupied && !isReserved) return;

      const rooms = req.assignedRooms?.length
        ? req.assignedRooms
        : req.assignedRoom
          ? [{ roomNumber: req.assignedRoom, roomType: '' }]
          : [];

      const pushEntry = (roomNumber, roomType) => {
        const entry = {
          guestHouseName: gh.name,
          roomNumber,
          roomType,
          roomsRequired: req.roomsRequired || 1,
          guestName: req.guestName || req.applicantName || 'Guest',
          requestId: req.requestId,
          checkIn: req.checkIn,
          checkOut: req.checkOut,
        };
        reserved.push(entry);
        if (isOccupied) occupied.push(entry);
      };

      if (rooms.length) {
        rooms.forEach(r => {
          claimedRoomNumbers.add(r.roomNumber);
          pushEntry(r.roomNumber, r.roomType);
        });
      } else {
        /* Status is booked but no specific room pinned down yet — still show
           it so the count always has something to explain. */
        pushEntry(null, '');
      }
    });

    gh.rooms
      .filter(r => r.status !== 'Maintenance' && !claimedRoomNumbers.has(r.roomNumber))
      .forEach(r => vacant.push({ guestHouseName: gh.name, roomNumber: r.roomNumber, roomType: r.roomType }));
  });

  return { vacant, reserved, occupied };
};

/* Color tracks what share of rooms are Reserved (booked at all, including
   checked-in guests): under a third is green, up to 85% is amber, past that is red. */
const cellColors = (s) => {
  if (s.total === 0) return { bg: '#e2e4e8', border: '#b9bec6' };
  const reservedPct = (s.reserved / s.total) * 100;
  if (reservedPct > 85) return { bg: '#f2a9a3', border: '#c9645c' };
  if (reservedPct > 33) return { bg: '#f7cd7a', border: '#c9932e' };
  return { bg: '#a9dab9', border: '#5fa878' };
};

function GhDropdown({ value, options, onChange }) {
  const ddOptions = [
    { key: 'All', value: 'All', text: 'All Guest Houses' },
    ...options.map(gh => ({ key: gh.name, value: gh.name, text: gh.name })),
  ];
  return (
    <Dropdown
      selection
      value={value}
      options={ddOptions}
      onChange={(_, data) => onChange(data.value)}
    />
  );
}

function AvailabilityCalendar({ bookingData }) {
  const { guestHouses } = bookingData;
  const [month, setMonth] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selectedGh, setSelectedGh] = useState('All');
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  const filtered = useMemo(() =>
    selectedGh === 'All' ? guestHouses : guestHouses.filter(gh => gh.name === selectedGh),
    [guestHouses, selectedGh]
  );

  const days = useMemo(() => buildDays(month), [month]);
  const roomDetails = useMemo(
    () => selectedDateKey ? getRoomDetails(filtered, selectedDateKey) : null,
    [filtered, selectedDateKey],
  );

  return (
    <Segment style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
        <Button
          icon="angle left"
          basic
          circular
          size="mini"
          onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
        />
        <Header as="h3" style={{ margin: 0 }}>{fmtMonth(month)}</Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GhDropdown value={selectedGh} options={guestHouses} onChange={setSelectedGh} />
          <Button
            icon="angle right"
            basic
            circular
            size="mini"
            onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, fontSize: '0.75rem', marginBottom: 6, flexShrink: 0 }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#a9dab9', border: '1px solid #5fa878', marginRight: 5 }} />Reserved 0–33%</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#f7cd7a', border: '1px solid #c9932e', marginRight: 5 }} />Reserved 33–85%</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#f2a9a3', border: '1px solid #c9645c', marginRight: 5 }} />Reserved 85%+</span>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'auto repeat(6, minmax(0, 1fr))', gap: 4 }}>
        {WEEK_DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', padding: '2px 2px 4px', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(0,0,0,0.5)' }}>
            {d}
          </div>
        ))}

        {days.map(day => {
          const s = getDayStats(filtered, day.key);
          const isToday = day.key === TODAY;
          const { bg, border } = cellColors(s);
          return (
            <div
              key={day.key}
              onClick={() => setSelectedDateKey(day.key)}
              style={{
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 6,
                minHeight: 0,
                overflow: 'hidden',
                padding: '4px 6px',
                cursor: 'pointer',
                opacity: day.inMonth ? 1 : 0.4,
                boxShadow: isToday ? 'inset 0 0 0 2px #2185d0' : undefined,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ fontWeight: isToday ? 700 : 600, fontSize: '1.1rem', textAlign: 'left', flexShrink: 0, color: '#1a1a1a' }}>
                {day.num}
              </div>
              {s.total > 0 && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', color: '#1a1a1a' }}>
                  <div>{s.vacant} Vacant</div>
                  <div>{s.reserved} Reserved</div>
                  <div>{s.occupied} Occupied</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        open={Boolean(selectedDateKey)}
        onClose={() => setSelectedDateKey(null)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            {selectedDateKey && fmtDateLabel(selectedDateKey)}
            <GhDropdown value={selectedGh} options={guestHouses} onChange={setSelectedGh} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          {roomDetails && (
            <>
              <Header as="h4" style={{ color: '#1b5e20' }}>Vacant ({roomDetails.vacant.length})</Header>
              {roomDetails.vacant.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 24 }}>
                  {roomDetails.vacant.map((r, i) => (
                    <Label key={i} basic color="green">
                      {r.roomNumber}{r.roomType ? ` · ${r.roomType}` : ''}
                      {selectedGh === 'All' && <Label.Detail>{r.guestHouseName}</Label.Detail>}
                    </Label>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'rgba(0,0,0,0.5)', marginBottom: 24 }}>No vacant rooms.</p>
              )}

              <Header as="h4" style={{ color: '#8a5a00' }}>Reserved ({roomDetails.reserved.length})</Header>
              {roomDetails.reserved.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 24 }}>
                  {roomDetails.reserved.map((r, i) => (
                    <Label key={i} basic color="yellow">
                      {r.roomNumber || `${r.roomsRequired} room(s)`}{r.roomType ? ` · ${r.roomType}` : ''} — {r.guestName}
                      {selectedGh === 'All' && <Label.Detail>{r.guestHouseName}</Label.Detail>}
                    </Label>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'rgba(0,0,0,0.5)', marginBottom: 24 }}>No reserved rooms.</p>
              )}

              <Header as="h4" style={{ color: '#8e1e1e' }}>Occupied ({roomDetails.occupied.length})</Header>
              {roomDetails.occupied.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {roomDetails.occupied.map((r, i) => (
                    <Label key={i} basic color="red">
                      {r.roomNumber || `${r.roomsRequired} room(s)`}{r.roomType ? ` · ${r.roomType}` : ''} — {r.guestName}
                      {selectedGh === 'All' && <Label.Detail>{r.guestHouseName}</Label.Detail>}
                    </Label>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'rgba(0,0,0,0.5)' }}>No occupied rooms.</p>
              )}
            </>
          )}
        </Modal.Content>
      </Modal>
    </Segment>
  );
}

export default AvailabilityCalendar;
